import React from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createStockItems } from '../../api/stock-itemsAPI';
import handleDownloadTemplateHelper from './handleDownloadTemplateHelper';

const StockItemsImporter = ({ onComplete }) => {
  const handleDataImport = async (rows, onProgress) => {
    let count = 0;
    for (const row of rows) {
      try {
        await createStockItems({
          qrCode: row.qrCode,
          stockMin: row.stockMin,
          stockMax: row.stockMax,
          stockImage: row.stockImage,
          stockDescription: row.stockDescription,
          locationId: row.locationId,
          unitCost: row.unitCost,
          measurementUnitId: row.measurementUnitId,
          currencyId: row.currencyId,
          serialised: row.serialised,
        });
      } catch (err) {
        console.error('Failed to import stock item', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>stock items</strong> into the system using an Excel or CSV file.
          This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className='mb-4'>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => handleDownloadTemplateHelper('stockitems')}
          >
            Download Template
          </button>
        </div>
        <div className="mb-4">
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>partId</td>
                      <td>Lookup</td>
                      <td>ID of the associated part</td>
                    </tr>
                    <tr>
                      <td>locationId</td>
                      <td>Lookup</td>
                      <td>ID of the warehouse/location</td>
                    </tr>
                    <tr>
                      <td>measurementUnitId</td>
                      <td>Lookup</td>
                      <td>ID of the measurement unit</td>
                    </tr>
                    <tr>
                      <td>currencyId</td>
                      <td>Lookup</td>
                      <td>ID of the currency</td>
                    </tr>
                    <tr>
                      <td>serialised</td>
                      <td>Boolean</td>
                      <td>Whether the item is serialised (1 or 0)</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>qrCode</td>
                      <td>Text</td>
                      <td>QR code value</td>
                    </tr>
                    <tr>
                      <td>stockMin</td>
                      <td>Number</td>
                      <td>Minimum stock level</td>
                    </tr>
                    <tr>
                      <td>stockMax</td>
                      <td>Number</td>
                      <td>Maximum stock level</td>
                    </tr>
                    <tr>
                      <td>stockImage</td>
                      <td>Text</td>
                      <td>Image filename or URL</td>
                    </tr>
                    <tr>
                      <td>stockDescription</td>
                      <td>Text</td>
                      <td>Description or notes</td>
                    </tr>
                    <tr>
                      <td>unitCost</td>
                      <td>Number</td>
                      <td>Unit cost of the item</td>
                    </tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity="stock-items"
          requiredFields={[
            { key: 'partId', type: 'lookup', required: true, api: 'parts', nameField: 'partNumber' },
            { key: 'locationId', type: 'lookup', required: true, api: 'locations', nameField: 'name' },
            { key: 'measurementUnitId', type: 'lookup', required: true, api: 'measurement-units', nameField: 'unitCode' },
            { key: 'currencyId', type: 'lookup', required: true, api: 'currency', nameField: 'symbol' },
            { key: 'serialised', type: 'boolean', required: true },
            { key: 'qrCode', type: 'string' },
            { key: 'stockMin', type: 'number' },
            { key: 'stockMax', type: 'number' },
            { key: 'stockImage', type: 'string' },
            { key: 'stockDescription', type: 'string' },
            { key: 'unitCost', type: 'number' },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
        />

      </div>
    </>
  );
};

export default StockItemsImporter;
